import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import * as flashActions from 'redux-flash'
import { onError } from 'lp-hoc'
import { logException } from 'sentry'
import {
  Content,
  FlashMessageContainer,
  SessionExpiryMonitor,
  ExternalLink,
  AccountMenu,
  Modal,
  RenderedHtml,
  PortalSelector,
  TeachingArtistPortalSelector,
  PartnerPortalSelector,
  FooterLegalese,
  NotificationsMenu,
} from 'components'
import * as apiActions from 'api-actions'
import { browserHistory } from 'react-router'
import { selectors as globalSelectors } from './global-reducer'
import {
  updateDocumentTitle,
  useCommunity,
  useCommunityText,
  displaySubmitFailure,
  initializeAuthenticatedUserflow,
  getCurrentEmployment,
} from 'utils'
import isLearningHubEnabled, {
  isSchoolLearningHubRestricted,
} from 'utils/local/is-learning-hub-enabled'
import LearningHubSecondaryNav from './learning-hub-secondary-nav'
import { Spinner } from 'lp-components'
import {
  ARTIST_PORTAL_ROUTE,
  EDUCATOR_PORTAL_ROUTE,
  PARTNER_PORTAL_ROUTE,
  SCHOOL_PORTAL_ROUTE,
  SURVEY_ATTEMPT_STATES,
} from 'config'
import { kebabCase, get } from 'lodash'
import { LearningHubNotificationsPreferencesForm } from 'forms'
import classnames from 'classnames'
import menuIcon from 'images/menu.svg'
import { LockIcon } from 'components/icons'
import * as LS from 'local-storage'

const propTypes = {
  currentSchoolId: PropTypes.number.isRequired,
  children: PropTypes.node,
  fetchSchool: PropTypes.func.isRequired,
  fetchEducatorUserDetails: PropTypes.func.isRequired,
  fetchArtistUserDetails: PropTypes.func.isRequired,
  fetchPartnerUserDetails: PropTypes.func.isRequired,
  flashMessages: PropTypes.arrayOf(flashActions.flashMessageType),
  isAdmin: PropTypes.bool,
  userDetails: Types.user,
  fetchNotifications: PropTypes.func.isRequired,
  markAllAsRead: PropTypes.func.isRequired,
  notifications: PropTypes.arrayOf(Types.notification),
  fetchNotificationPreferences: PropTypes.func.isRequired,
  updateNotificationPreferences: PropTypes.func.isRequired,
  notificationPreferences: Types.notificationPreferences,
  school: PropTypes.object,
  learningHubCatalog: PropTypes.object,
  fetchLearningHubCourses: PropTypes.func.isRequired,
}

const defaultProps = {
  children: null,
  flashMessages: [],
  isAdmin: false,
  userDetails: null,
  school: null,
  learningHubCatalog: undefined,
}

function LayoutAuthorized({
  children,
  currentSchoolId,
  flashMessages,
  isAdmin,
  fetchSchool,
  fetchEducatorUserDetails,
  fetchArtistUserDetails,
  fetchPartnerUserDetails,
  userDetails,
  fetchNotifications,
  markAllAsRead,
  notifications,
  fetchNotificationPreferences,
  notificationPreferences,
  updateNotificationPreferences,
  school,
  learningHubCatalog,
  fetchLearningHubCourses,
}) {
  const community = useCommunity()
  const t = useCommunityText()
  const terms = t('terms')
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [showPreferencesModal, setShowPreferencesModal] = useState(false)
  const [dismissedLockedModal, setDismissedLockedModal] = useState(false)
  const [holdForRedirect, setHoldForRedirect] = useState(!!terms)
  const isArtistSession = !!LS.getArtistAuthToken() && !LS.getSchoolAuthToken()
  // Organization users sign in under the partner portal token (no school or
  // artist token). They have an Organization employment, not a School one.
  const isPartnerSession =
    !isArtistSession && !LS.getSchoolAuthToken() && !!LS.getPartnerAuthToken()
  const currentArtistId = Number(LS.getCurrentArtistId())
  const currentPartnerId = Number(LS.getCurrentPartnerId())

  // Only educator/school sessions are backed by a School entity (used for the
  // restrict-access survey lock). Artist and partner sessions skip it.
  const currentEmployableType = isArtistSession
    ? Types.EMPLOYABLE_TYPES.TEACHING_ARTIST
    : isPartnerSession
    ? Types.EMPLOYABLE_TYPES.ORGANIZATION
    : Types.EMPLOYABLE_TYPES.SCHOOL
  const currentEmployableId = isArtistSession
    ? currentArtistId
    : isPartnerSession
    ? currentPartnerId
    : currentSchoolId

  useEffect(() => {
    if (isArtistSession || isPartnerSession) return
    fetchSchool(currentSchoolId)
  }, [currentSchoolId, isArtistSession, isPartnerSession, fetchSchool])

  useEffect(() => {
    fetchNotifications()
    const pollInterval = setInterval(fetchNotifications, 60_000)
    const onFocus = () => fetchNotifications()
    window.addEventListener('focus', onFocus)
    return () => {
      clearInterval(pollInterval)
      window.removeEventListener('focus', onFocus)
    }
  }, [fetchNotifications])

  useEffect(() => {
    if (isArtistSession) {
      fetchArtistUserDetails()
    } else if (isPartnerSession) {
      fetchPartnerUserDetails()
    } else {
      fetchEducatorUserDetails()
    }
  }, [
    isArtistSession,
    isPartnerSession,
    fetchArtistUserDetails,
    fetchPartnerUserDetails,
    fetchEducatorUserDetails,
  ])

  useEffect(() => {
    if (terms && userDetails) {
      if (!userDetails.agreedAt) {
        browserHistory.replace(
          isArtistSession
            ? `${ARTIST_PORTAL_ROUTE}/terms`
            : isPartnerSession
            ? `${PARTNER_PORTAL_ROUTE}/terms`
            : `${EDUCATOR_PORTAL_ROUTE}/terms`
        )
      } else {
        setHoldForRedirect(false)
      }
    }
  }, [userDetails, terms, isArtistSession, isPartnerSession])

  const currentEmployment = useMemo(() => {
    if (!userDetails) return
    return getCurrentEmployment({
      employments: userDetails.employments,
      currentEmployableId,
      currentEmployableType,
    })
  }, [userDetails, currentEmployableId, currentEmployableType])

  useEffect(() => {
    if (!currentEmployment) return
    if (!userDetails?.notificationSubscriptionId) return
    fetchNotificationPreferences(userDetails.notificationSubscriptionId)
  }, [
    currentEmployment,
    userDetails?.notificationSubscriptionId,
    fetchNotificationPreferences,
  ])

  const hasSessionMatchedUserDetails = !!userDetails?.employments?.some(
    (employment) =>
      employment?.employableType === currentEmployableType &&
      Number(employment?.employable?.id) === Number(currentEmployableId)
  )

  const entityHomeRoute = useMemo(() => {
    if (isArtistSession) return `${ARTIST_PORTAL_ROUTE}/dashboard`
    if (isPartnerSession) return `${PARTNER_PORTAL_ROUTE}/dashboard`
    if (!currentEmployment) return `${EDUCATOR_PORTAL_ROUTE}/home`
    if (currentEmployment.position === Types.POSITION_TYPES.PRINCIPAL) {
      return `${SCHOOL_PORTAL_ROUTE}/dashboard`
    }
    return `${EDUCATOR_PORTAL_ROUTE}/home`
  }, [currentEmployment, isArtistSession, isPartnerSession])

  const learningHubAccess = isLearningHubEnabled(
    userDetails,
    isArtistSession || isPartnerSession ? null : school
  )

  const supportEmail = isArtistSession
    ? community.artistSupportEmail
    : isPartnerSession
    ? community.partnerSupportEmail
    : community.schoolSupportEmail
  const deniedAccess = hasSessionMatchedUserDetails && !learningHubAccess

  useEffect(() => {
    if (!hasSessionMatchedUserDetails || !learningHubAccess) return
    if (learningHubCatalog !== undefined) return
    fetchLearningHubCourses()
  }, [
    hasSessionMatchedUserDetails,
    learningHubAccess,
    learningHubCatalog,
    fetchLearningHubCourses,
  ])
  const isSurveyLocked =
    isSchoolLearningHubRestricted(school) &&
    get(school, 'surveyAttempt.state') === SURVEY_ATTEMPT_STATES.IN_PROGRESS

  useEffect(() => {
    if (!isSurveyLocked && dismissedLockedModal) {
      setDismissedLockedModal(false)
    }
  }, [isSurveyLocked, dismissedLockedModal])

  useEffect(() => {
    if (deniedAccess) browserHistory.replace(entityHomeRoute)
  }, [deniedAccess, entityHomeRoute])

  if (hasSessionMatchedUserDetails && !currentEmployment) {
    browserHistory.replace(entityHomeRoute)
  }

  initializeAuthenticatedUserflow(userDetails)

  const [navVisibleOnMobile, setNavVisibleOnMobile] = useState(false)
  const closeMobileNav = () => {
    setNavVisibleOnMobile(false)
    return true
  }

  if (deniedAccess) {
    return (
      <div className={kebabCase(Types.PORTAL.LEARNING_HUB)}>
        <Spinner />
      </div>
    )
  }

  return (
    <div className={kebabCase(Types.PORTAL.LEARNING_HUB)}>
      <a href="#content" id="skip-nav-link">
        Skip to Main Content
      </a>
      <button
        type="button"
        className="nav-toggle"
        aria-expanded={navVisibleOnMobile}
        aria-controls="side-navigation-content"
        onClick={() => setNavVisibleOnMobile(true)}
        aria-label="Open Navigation"
      >
        <img src={menuIcon} alt="" />
      </button>
      <div
        id="side-navigation-content"
        className={classnames('nav-container', {
          'is-active': navVisibleOnMobile,
        })}
      >
        <button
          type="button"
          className="close"
          aria-label="Close Navigation"
          onClick={closeMobileNav}
        >
          ×
        </button>
        {isArtistSession ? (
          <TeachingArtistPortalSelector
            currentPortal={Types.PORTAL.LEARNING_HUB}
            learningHubAccess={userDetails != null ? learningHubAccess : true}
          />
        ) : isPartnerSession ? (
          <PartnerPortalSelector
            currentPortal={Types.PORTAL.LEARNING_HUB}
            learningHubAccess={userDetails != null ? learningHubAccess : true}
          />
        ) : (
          <PortalSelector
            currentPortal={Types.PORTAL.LEARNING_HUB}
            currentEmployment={currentEmployment}
            learningHubAccess={userDetails != null ? learningHubAccess : true}
          />
        )}
        <LearningHubSecondaryNav
          community={community}
          entityHomeRoute={entityHomeRoute}
          closeMobileNav={closeMobileNav}
        />
      </div>
      {!hasSessionMatchedUserDetails || holdForRedirect ? (
        <Spinner />
      ) : (
        <div className="portal-select-enabled">
          {isAdmin && (
            <div className="flash-message admin failure">
              <p>You are viewing the portal as an admin</p>
            </div>
          )}
          <FlashMessageContainer messages={flashMessages} />
          <SessionExpiryMonitor
            redirectToSignOut={() =>
              browserHistory.replace(
                isArtistSession
                  ? `${ARTIST_PORTAL_ROUTE}/sign-out`
                  : isPartnerSession
                  ? `${PARTNER_PORTAL_ROUTE}/sign-out`
                  : `${SCHOOL_PORTAL_ROUTE}/sign-out`
              )
            }
          />
          <Content id="content" className="portal-wrapper">
            {notificationPreferences && (
              <NotificationsMenu
                openPreferencesModal={(show) => setShowPreferencesModal(show)}
                notifications={notifications}
                markAllAsRead={markAllAsRead}
              />
            )}
            <AccountMenu
              currentEmployment={currentEmployment}
              currentEmployableType={currentEmployableType}
              employments={userDetails.employments}
              userProfile={userDetails.profile}
              userEmail={userDetails.email}
            />
            {children}
            <footer className="portal-footer">
              <FooterLegalese
                hasTerms={!!terms}
                openTermsModal={() => setShowTermsModal(true)}
              />
              <p className="contact">
                Email{' '}
                <ExternalLink href={`mailto:${supportEmail}`}>
                  {supportEmail}
                </ExternalLink>{' '}
                with questions.
              </p>
            </footer>
            {showTermsModal && (
              <Modal onClose={() => setShowTermsModal(false)}>
                <h2>Terms & Conditions</h2>
                <div className="modal-content">
                  <RenderedHtml>{terms}</RenderedHtml>
                </div>
              </Modal>
            )}
            {showPreferencesModal && (
              <Modal onClose={() => setShowPreferencesModal(false)}>
                <LearningHubNotificationsPreferencesForm
                  initialValues={notificationPreferences}
                  onSubmit={(params) =>
                    updateNotificationPreferences(
                      notificationPreferences.id,
                      params
                    )
                  }
                  onSubmitSuccess={() => setShowPreferencesModal(false)}
                  onSubmitFail={displaySubmitFailure}
                  closePreferencesModal={() => setShowPreferencesModal(false)}
                />
              </Modal>
            )}
            {!isArtistSession &&
              !isPartnerSession &&
              isSurveyLocked &&
              !dismissedLockedModal && (
                <Modal
                  onClose={() => setDismissedLockedModal(true)}
                  className="learning-hub-locked-modal-wrapper"
                >
                  <div className="learning-hub-locked-modal">
                    <LockIcon />
                    <h2>Your Learning Hub content is waiting!</h2>
                    <p>
                      To unlock your Learning Hub content, please return to your
                      school portal and complete your school&apos;s survey.
                    </p>
                    <button
                      type="button"
                      className="button-primary"
                      onClick={() =>
                        browserHistory.push(
                          `${SCHOOL_PORTAL_ROUTE}/survey/get-started`
                        )
                      }
                    >
                      Take the Survey
                    </button>
                  </div>
                </Modal>
              )}
          </Content>
        </div>
      )}
    </div>
  )
}

LayoutAuthorized.propTypes = propTypes
LayoutAuthorized.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    currentSchoolId: globalSelectors.currentSchoolId(state),
    flashMessages: flashActions.getFlashMessages(state),
    isAdmin: globalSelectors.isAdmin(state),
    school: globalSelectors.school(state),
    userDetails: globalSelectors.userDetails(state),
    notifications: globalSelectors.notifications(state),
    notificationPreferences: globalSelectors.notificationPreferences(state),
    learningHubCatalog: globalSelectors.learningHubCatalog(state),
  }
}

const mapDispatchToProps = {
  fetchSchool: apiActions.fetchSchoolAuthorized,
  fetchEducatorUserDetails: apiActions.fetchCurrentEducatorUserDetails,
  fetchArtistUserDetails: apiActions.fetchCurrentArtistUserDetails,
  fetchPartnerUserDetails: apiActions.fetchCurrentPartnerUserDetails,
  fetchNotifications: apiActions.fetchNotifications,
  fetchNotificationPreferences: apiActions.fetchNotificationPreferences,
  updateNotificationPreferences: apiActions.updateNotificationPreferences,
  markAllAsRead: apiActions.markAllAsRead,
  fetchLearningHubCourses: apiActions.fetchLearningHubCourses,
}

function updateTitle({ school }) {
  const prefix = school?.name ? `${school.name} - ` : ''
  return `${prefix}Learning Hub | artlook`
}

function onComponentDidCatch(_, error, errorInfo) {
  return logException(error, errorInfo)
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  updateDocumentTitle(updateTitle),
  onError(onComponentDidCatch)
)(LayoutAuthorized)
